import type { ModelLanguage } from '~/model/language'

export class ServiceLanguage {
    public static async getLanguageList(): Promise<[any, ModelLanguage[]]> {
        try {
            const { data: languages, error } = await useLazyFetch<ModelLanguage[]>(
                '/dil',
                {
                    key: 'languages', 
                    server: true,
                    default: () => [],
                    baseURL: useRuntimeConfig().public.baseUrl,
                    transform: (data: any) => data?.data || []
                }
            )

            if (error.value) {
                return [error.value, []]
            }

            return [null, languages.value || []]
        } catch (error) {
            return [error, []]
        }
    }
}

export const useLanguages = () => {
    const { locale } = useI18n()
    
    const languagesRef = useState<ModelLanguage[]>('app.languages', () => [])
    const errorRef = useState<any>('app.languages.error', () => null)
    const loadingRef = useState<boolean>('app.languages.loading', () => false)
    
    const loadLanguages = async () => {
        if (languagesRef.value.length > 0 || loadingRef.value) {
            return
        }
        
        loadingRef.value = true
        const [err, languages] = await ServiceLanguage.getLanguageList()
        loadingRef.value = false
        
        if (err) {
            errorRef.value = err
            languagesRef.value = [] 
        } else {
            languagesRef.value = languages
            errorRef.value = null
        }
    }
    
    if (languagesRef.value.length === 0) {
        loadLanguages()
    }
    
    const visibleLanguages = computed<ModelLanguage[]>(() => 
        languagesRef.value.slice(0, 5)
    )
    
    const dropdownLanguages = computed<ModelLanguage[]>(() => 
        languagesRef.value.slice(5)
    )
    
    const hasDropdown = computed<boolean>(() => 
        languagesRef.value.length > 5
    )
    
    const isRTL = computed<boolean>(() => {
        const currentLang = languagesRef.value.find(lang => lang.kod === locale.value)
        return currentLang?.sagdan_mi || false
    })
    
    const currentDirection = computed<string>(() => 
        isRTL.value ? 'rtl' : 'ltr'
    )
    
    const activeLanguageIndex = computed<number>(() => {
        const index = languagesRef.value.findIndex(lang => lang.kod === locale.value)
        return index >= 0 ? index : 0
    })
    
    return {
        visibleLanguages,
        dropdownLanguages,
        hasDropdown,
        
        isRTL,
        currentDirection,
        
        activeLanguageIndex,
        
        error: readonly(errorRef)
    }
}